import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    BarChart3,
    Bell,
    Building2,
    Calculator,
    CalendarDays,
    ChevronDown,
    ClipboardCheck,
    ClipboardList,
    Coins,
    CreditCard,
    FileText,
    Filter,
    HardHat,
    HeartHandshake,
    ListChecks,
    Mail,
    Menu,
    MessageSquare,
    Package,
    PieChart,
    Receipt,
    RefreshCw,
    Route,
    Search,
    ShieldCheck,
    ShoppingCart,
    Smartphone,
    Sparkles,
    Store,
    TrendingUp,
    Truck,
    UserCheck,
    UserCircle,
    UserRound,
    Users,
    Warehouse,
    Waves,
    Workflow,
    Wrench,
    X,
    Zap,
} from "lucide-react";
import { Logo } from "@/components/Logo";

const EASE = [0.22, 1, 0.36, 1];

export const MEGA_GROUPS = [
    {
        title: "Sales & Marketing",
        items: [
            { icon: Users, name: "CRM & Enquiries", desc: "Capture and convert more business", link: "/platform/crm-enquiries" },
            { icon: Filter, name: "Sales Pipeline", desc: "Track opportunities and win more jobs", link: "/platform/sales-pipeline" },
            { icon: FileText, name: "Quotes & Invoicing", desc: "Create quotes and get paid faster", link: "/platform/quotes-invoicing" },
            { icon: Sparkles, name: "AI Marketing Automation", desc: "Automate campaigns on real customer data", link: "/platform/ai-marketing-automation" },
            { icon: MessageSquare, name: "Email Campaigns", desc: "Send targeted campaigns that convert", link: "/platform/email-campaigns" },
            { icon: UserRound, name: "Customer Segmentation", desc: "Target the right customers every time", link: "/platform/customer-segmentation" },
            { icon: Route, name: "Customer Journey Automation", desc: "Follow up every customer automatically", link: "/platform/customer-journey-automation" },
            { icon: TrendingUp, name: "Customer Revenue Intelligence", desc: "See value, churn risk and upsell signals", link: "/platform/customer-revenue-intelligence" },
            { icon: UserCircle, name: "Customer Portal", desc: "Give customers secure self-service access", link: "/platform/customer-portal" },
        ],
    },
    {
        title: "Operations & Field",
        items: [
            { icon: ClipboardList, name: "Job Management", desc: "Manage jobs from enquiry to completion", link: "/platform/job-management" },
            { icon: Users, name: "Scheduling & Dispatch", desc: "Optimise teams and appointments", link: "/platform/scheduling-dispatch" },
            { icon: Smartphone, name: "TedOps Field App", desc: "Everything your engineers need", link: "/platform/field-app" },
            { icon: ClipboardCheck, name: "Digital Forms & Checklists", desc: "Capture data in the field", link: "/platform/digital-forms" },
            { icon: ListChecks, name: "Tasks & Workflows", desc: "Standardise and automate work", link: "/platform/tasks-workflows" },
            { icon: UserRound, name: "Customer Management", desc: "Keep all customer information in one place", link: "/platform/customer-management" },
            { icon: Building2, name: "Multi-Company Management", desc: "Run multiple sites and teams", link: "/platform/multi-company-management" },
            { icon: ShieldCheck, name: "Health & Safety Compliance", desc: "Keep every team safe and audit-ready", link: "/platform/health-safety-compliance" },
            { icon: UserCheck, name: "HR & Staff Management", desc: "Records, absence and training in one place", link: "/platform/hr-staff-management" },
        ],
    },
    {
        title: "Finance & Commerce",
        items: [
            { icon: Calculator, name: "Finance Management", desc: "From daily operations to financial clarity", link: "/platform/finance-management" },
            { icon: PieChart, name: "Management P&L", desc: "Live profit and loss across the business", link: "/platform/management-pnl" },
            { icon: Receipt, name: "Bills & Expenses", desc: "Capture and control every cost", link: "/platform/bills-expenses" },
            { icon: ShoppingCart, name: "E-commerce Order Management", desc: "Unify online and offline orders", link: "/platform/ecommerce-order-management" },
            { icon: RefreshCw, name: "Subscriptions & Recurring Revenue", desc: "Automate recurring billing and renewals", link: "/platform/subscriptions-recurring-revenue" },
            { icon: CreditCard, name: "Retail EPOS", desc: "Sell in-store and on the go", link: "/platform/retail-epos" },
        ],
    },
    {
        title: "Stock & Warehouse",
        items: [
            { icon: Package, name: "Stock & Inventory", desc: "Track stock across sites and vehicles", link: "/platform/stock-inventory" },
            { icon: Warehouse, name: "Picking & Loading", desc: "Prepare jobs with confidence", link: "/platform/picking-loading" },
            { icon: Truck, name: "Returns & Dispatch", desc: "Manage returns and outbound deliveries", link: "/platform/returns-dispatch" },
        ],
    },
    {
        title: "Intelligence & AI",
        items: [
            { icon: Sparkles, name: "AI Operations Assistant", desc: "Get answers and take action, instantly", link: "/platform/ai-operations-assistant" },
            { icon: BarChart3, name: "AI Insights & Recommendations", desc: "Spot issues and opportunities early", link: "/platform/ai-insights" },
            { icon: Workflow, name: "Intelligent Automation", desc: "Automate everyday tasks and updates", link: "/platform/intelligent-automation" },
            { icon: Bell, name: "Smart Notifications", desc: "Stay ahead with proactive alerts", link: "/platform/smart-notifications" },
            { icon: TrendingUp, name: "Analytics & Reporting", desc: "Real-time visibility across your business", link: "/platform/analytics-reporting" },
        ],
    },
];

const SOLUTIONS_GROUPS = [
    {
        title: "By Business Need",
        items: [
            { icon: MessageSquare, name: "CRM & Enquiries", desc: "Capture and convert every enquiry", link: "/solutions/crm-enquiries" },
            { icon: BarChart3, name: "Stop missed enquiries", desc: "Capture and respond to every lead", link: "/solutions/stop-missed-enquiries" },
            { icon: Zap, name: "Speed up scheduling", desc: "Plan teams and jobs in less time", link: "/solutions/speed-up-scheduling" },
            { icon: FileText, name: "Reduce admin workload", desc: "Automate repetitive office tasks", link: "/solutions/reduce-admin-workload" },
            { icon: Coins, name: "Get paid faster", desc: "Speed up quotes, invoices and payments", link: "/solutions/get-paid-faster" },
            { icon: Package, name: "Improve stock control", desc: "Know what you have, where it is", link: "/solutions/improve-stock-control" },
            { icon: HeartHandshake, name: "Improve customer retention", desc: "Keep more of the customers you win", link: "/solutions/improve-customer-retention" },
            { icon: TrendingUp, name: "Recover lost sales", desc: "Stop quotes and deals going cold", link: "/solutions/recover-lost-sales-opportunities" },
            { icon: PieChart, name: "Understand profitability", desc: "See live margin across your business", link: "/solutions/understand-business-profitability" },
            { icon: Zap, name: "Automate follow-ups", desc: "Chase every customer automatically", link: "/solutions/automate-customer-follow-ups" },
            { icon: Mail, name: "Reduce marketing admin", desc: "Campaigns without the manual work", link: "/solutions/reduce-marketing-administration" },
            { icon: ShoppingCart, name: "Connect online & offline", desc: "One order flow across every channel", link: "/solutions/connect-online-offline-sales" },
        ],
    },
    {
        title: "By Team",
        items: [
            { icon: Users, name: "Sales & admin", desc: "Win and manage more work", link: "/solutions/sales-admin" },
            { icon: HardHat, name: "Operations managers", desc: "See and control every job", link: "/solutions/operations-manager" },
            { icon: Wrench, name: "Field engineers", desc: "Everything needed on site", link: "/solutions/field-engineers" },
            { icon: Package, name: "Warehouse teams", desc: "Pick, pack and dispatch accurately", link: "/solutions/warehouse-teams" },
            { icon: Calculator, name: "Finance teams", desc: "Faster, cleaner financial workflows", link: "/solutions/finance-teams" },
        ],
    },
    {
        title: "By Workflow",
        items: [
            { icon: CalendarDays, name: "Scheduling & Dispatch", desc: "Optimise teams and appointments", link: "/platform/scheduling-dispatch" },
            { icon: FileText, name: "Quotes & Invoicing", desc: "Create quotes and get paid faster", link: "/platform/quotes-invoicing" },
            { icon: ClipboardCheck, name: "Digital Forms & Checklists", desc: "Capture data in the field", link: "/platform/digital-forms" },
            { icon: Package, name: "Stock & Inventory", desc: "Track stock across sites and vehicles", link: "/platform/stock-inventory" },
        ],
    },
];

const INDUSTRY_ITEMS = [
    { icon: Truck, name: "Field Services", desc: "For service, maintenance and mobile teams", link: "/industries/field-services" },
    { icon: Wrench, name: "Installation & Maintenance", desc: "Manage installs, service visits and follow-up work", link: "/industries/installation-maintenance" },
    { icon: Store, name: "Retail & Trade", desc: "Unify shop, stock and customer operations", link: "/industries/retail-trade" },
    { icon: Building2, name: "Multi-Site Businesses", desc: "Control teams, workflows and visibility across locations", link: "/industries/multi-site" },
    { icon: Waves, name: "Hot Tub & Wellness", desc: "Run sales, installs, servicing and warranties", link: "/industries/hot-tub-wellness" },
];

const SIMPLE_MENUS = {
    resources: [
        { name: "Insights & News", link: "/blog" },
        { name: "AI Insights", link: "/ai-insights" },
        { name: "Guides", link: "/guides" },
        { name: "Integrations", link: "/integrations" },
        { name: "Knowledge Centre", link: "/knowledge-centre" },
        { name: "Pricing", link: "/pricing" },
    ],
    company: [
        { name: "About TedOps", link: "/about" },
        { name: "Careers", link: "/careers" },
        { name: "What's New", link: "/updates" },
        { name: "Contact", link: "/contact" },
    ],
};

const NAV = [
    { id: "platform", label: "Platform", mega: true },
    { id: "solutions", label: "Solutions", solutions: true },
    { id: "industries", label: "Industries", industries: true },
    { id: "resources", label: "Resources" },
    { id: "company", label: "Company" },
];

const SEARCH_ITEMS = [
    ...MEGA_GROUPS.flatMap((g) => g.items.map((i) => ({ name: i.name, desc: i.desc, link: i.link, group: g.title }))),
    ...SOLUTIONS_GROUPS.flatMap((g) => g.items.map((i) => ({ name: i.name, desc: i.desc, link: i.link, group: `Solutions · ${g.title}` }))),
    ...INDUSTRY_ITEMS.map((i) => ({ name: i.name, desc: i.desc, link: i.link, group: "Industries" })),
    { name: "Pricing", desc: "Plans that grow with your business", link: "/pricing", group: "Resources" },
    { name: "Guides", desc: "Practical guides for service businesses", link: "/guides", group: "Resources" },
    { name: "Integrations", desc: "Connect your existing tools", link: "/integrations", group: "Resources" },
    { name: "Knowledge Centre", desc: "Set-up and how-to articles", link: "/knowledge-centre", group: "Resources" },
    { name: "Insights & News", desc: "Articles and product thinking", link: "/blog", group: "Resources" },
    { name: "AI Insights", desc: "How AI helps real businesses", link: "/ai-insights", group: "Resources" },
    { name: "Contact", desc: "Talk to the TedOps team", link: "/contact", group: "Company" },
    { name: "About TedOps", desc: "Who we are and what we build", link: "/about", group: "Company" },
    { name: "Careers", desc: "Join the team", link: "/careers", group: "Company" },
    { name: "What's New", desc: "Latest product updates", link: "/updates", group: "Company" },
];

const SiteSearch = ({ onClose }) => {
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const results = query.trim()
        ? SEARCH_ITEMS.filter((i) =>
              `${i.name} ${i.desc} ${i.group}`.toLowerCase().includes(query.trim().toLowerCase()),
          ).slice(0, 8)
        : [];

    const go = (item) => {
        if (!item) return;
        onClose();
        navigate(item.link);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-x-4 top-[84px] z-50 mx-auto max-w-xl overflow-hidden rounded-2xl border border-[#0F172A]/[0.08] bg-white shadow-[0_30px_80px_-20px_rgba(11,18,32,0.4)]"
            data-testid="site-search"
        >
            <div className="flex items-center gap-3 border-b border-[#0F172A]/[0.06] px-5 py-4">
                <Search className="h-4 w-4 shrink-0 text-[#94A3B8]" strokeWidth={2} />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(0);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") onClose();
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setActive((a) => Math.min(a + 1, results.length - 1));
                        }
                        if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActive((a) => Math.max(a - 1, 0));
                        }
                        if (e.key === "Enter") go(results[active]);
                    }}
                    placeholder="Search pages, modules and solutions…"
                    className="w-full bg-transparent text-sm font-medium text-[#0F172A] outline-none placeholder:text-[#94A3B8]"
                    data-testid="site-search-input"
                />
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close search"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#94A3B8] transition-colors hover:bg-[#F7F8FA] hover:text-[#0F172A]"
                    data-testid="site-search-close"
                >
                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
                {query.trim() === "" ? (
                    <p className="px-3 py-6 text-center text-xs text-[#94A3B8]" data-testid="site-search-hint">
                        Try "pipeline", "marketing", "invoicing" or "pricing"
                    </p>
                ) : results.length === 0 ? (
                    <p className="px-3 py-6 text-center text-xs text-[#94A3B8]" data-testid="site-search-empty">
                        No matches for "{query}"
                    </p>
                ) : (
                    results.map((r, i) => (
                        <button
                            key={r.link}
                            type="button"
                            onClick={() => go(r)}
                            onMouseEnter={() => setActive(i)}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 ${
                                i === active ? "bg-[#F0F7FF]" : ""
                            }`}
                            data-testid={`site-search-result-${i}`}
                        >
                            <span className="min-w-0">
                                <span className="block truncate text-sm font-bold text-[#0F172A]">{r.name}</span>
                                <span className="block truncate text-xs text-[#64748B]">{r.desc}</span>
                            </span>
                            <span className="ml-auto shrink-0 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[#94A3B8]">
                                {r.group}
                            </span>
                        </button>
                    ))
                )}
            </div>
        </motion.div>
    );
};

export const MegaItem = ({ item, onSelect }) => {
    const Icon = item.icon;
    const inner = (
        <>
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0066FF]/10">
                <Icon className="h-4 w-4 text-[#0066FF]" strokeWidth={1.75} />
            </span>
            <span>
                <span className="block text-sm font-bold text-[#0F172A]">{item.name}</span>
                <span className="block text-xs leading-snug text-[#64748B]">{item.desc}</span>
            </span>
        </>
    );
    const cls = "group flex items-start gap-3 rounded-xl p-2 transition-colors duration-200 hover:bg-[#F0F7FF]";
    return item.link ? (
        <Link to={item.link} onClick={onSelect} className={cls} data-testid={`mega-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {inner}
        </Link>
    ) : (
        <span className={`${cls} cursor-pointer`}>{inner}</span>
    );
};

const MegaMenu = ({ onSelect }) => (
    <div className="grid grid-cols-5 gap-8 p-8">
        {MEGA_GROUPS.map((group) => (
            <div key={group.title}>
                <p className="border-b border-[#0F172A]/[0.06] pb-3 font-mono-brand text-[10px] font-medium uppercase tracking-[0.22em] text-[#94A3B8]">
                    {group.title}
                </p>
                <ul className="mt-4 space-y-1">
                    {group.items.map((item) => (
                        <li key={item.name}>
                            <MegaItem item={item} onSelect={onSelect} />
                        </li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
);

const SolutionItem = ({ item, onSelect }) => {
    const Icon = item.icon;
    const inner = (
        <>
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0066FF]/10">
                <Icon className="h-4 w-4 text-[#0066FF]" strokeWidth={1.75} />
            </span>
            <span>
                <span className="block text-sm font-bold text-[#0F172A]">{item.name}</span>
                <span className="block text-xs leading-snug text-[#64748B]">{item.desc}</span>
            </span>
        </>
    );
    const cls = "group flex items-start gap-3 rounded-xl p-2 transition-colors duration-200 hover:bg-[#F0F7FF]";
    return item.link ? (
        <Link to={item.link} onClick={onSelect} className={cls} data-testid={`solution-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {inner}
        </Link>
    ) : (
        <span className={`${cls} cursor-pointer`} data-testid={`solution-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {inner}
        </span>
    );
};

const SolutionsMenu = ({ onSelect }) => (
    <div className="grid grid-cols-3 gap-8 p-8" data-testid="solutions-menu">
        {SOLUTIONS_GROUPS.map((group) => (
            <div key={group.title}>
                <p className="border-b border-[#0F172A]/[0.06] pb-3 font-mono-brand text-[10px] font-medium uppercase tracking-[0.22em] text-[#94A3B8]">
                    {group.title}
                </p>
                <ul className="mt-4 space-y-1">
                    {group.items.map((item) => (
                        <li key={item.name}>
                            <SolutionItem item={item} onSelect={onSelect} />
                        </li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
);

export default function Header() {
    const [open, setOpen] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileSection, setMobileSection] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(null);
                setMobileOpen(false);
                setSearchOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    return (
        <>
        <motion.header
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9, ease: EASE }}
            className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#060A12]/80 backdrop-blur-md"
            data-testid="site-header"
        >
            <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-4 sm:gap-4 sm:px-10">
                <Link to="/" aria-label="TedOps AI home" data-testid="header-logo-link">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary" data-testid="main-nav">
                    {NAV.map((item) => (
                        <div
                            key={item.id}
                            className="relative"
                            onMouseEnter={() => setOpen(item.id)}
                            onMouseLeave={() => setOpen(null)}
                        >
                            <button
                                type="button"
                                aria-expanded={open === item.id}
                                aria-haspopup="true"
                                onClick={() => setOpen(open === item.id ? null : item.id)}
                                className={`relative flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors duration-200 ${
                                    open === item.id ? "text-[#00E5FF]" : "text-[#F8FAFF] hover:text-[#00E5FF]"
                                }`}
                                data-testid={`nav-${item.id}`}
                            >
                                {item.label}
                                <ChevronDown
                                    className={`h-3.5 w-3.5 transition-transform duration-200 ${open === item.id ? "rotate-180" : ""}`}
                                />
                                {open === item.id && (
                                    <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#0066FF]" />
                                )}
                            </button>

                            <AnimatePresence>
                                {open === item.id && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.18, ease: "easeOut" }}
                                        className={
                                            item.mega
                                                ? "fixed inset-x-4 top-[84px] mx-auto max-w-[1240px] overflow-hidden rounded-2xl border border-[#0F172A]/[0.08] bg-white shadow-[0_30px_80px_-20px_rgba(11,18,32,0.4)] sm:inset-x-10"
                                                : item.solutions
                                                  ? "fixed inset-x-4 top-[84px] mx-auto max-w-[980px] overflow-hidden rounded-2xl border border-[#0F172A]/[0.08] bg-white shadow-[0_30px_80px_-20px_rgba(11,18,32,0.4)] sm:inset-x-10"
                                                  : item.industries
                                                    ? "absolute left-0 top-full mt-2 w-[380px] overflow-hidden rounded-2xl border border-[#0F172A]/[0.08] bg-white p-2 shadow-[0_30px_80px_-20px_rgba(11,18,32,0.4)]"
                                                    : "absolute left-0 top-full mt-2 w-64 overflow-hidden rounded-2xl border border-[#0F172A]/[0.08] bg-white p-2 shadow-[0_20px_50px_-16px_rgba(11,18,32,0.35)]"
                                        }
                                        data-testid={item.mega ? "mega-menu" : item.solutions ? "solutions-mega-menu" : `menu-${item.id}`}
                                    >
                                        {item.mega ? (
                                            <>
                                                <MegaMenu onSelect={() => setOpen(null)} />
                                                <div className="flex justify-end border-t border-[#0F172A]/[0.06] px-8 py-4">
                                                    <span className="inline-flex cursor-pointer items-center gap-2 text-sm font-bold text-[#0066FF] transition-colors hover:text-[#A855F7]">
                                                        Explore All Platform Features <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                </div>
                                            </>
                                        ) : item.solutions ? (
                                            <SolutionsMenu onSelect={() => setOpen(null)} />
                                        ) : item.industries ? (
                                            <>
                                                <p className="mt-2 border-b border-[#0F172A]/[0.06] px-2 pb-3 font-mono-brand text-[10px] font-medium uppercase tracking-[0.22em] text-[#94A3B8]" data-testid="menu-title-industries">
                                                    Industries
                                                </p>
                                                <ul className="mt-2 space-y-1">
                                                {INDUSTRY_ITEMS.map((ind) => {
                                                    const Icon = ind.icon;
                                                    const inner = (
                                                        <>
                                                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0066FF]/10">
                                                                <Icon className="h-4 w-4 text-[#0066FF]" strokeWidth={1.75} />
                                                            </span>
                                                            <span>
                                                                <span className="block text-sm font-bold text-[#0F172A]">{ind.name}</span>
                                                                <span className="block text-xs leading-snug text-[#64748B]">{ind.desc}</span>
                                                            </span>
                                                        </>
                                                    );
                                                    const cls =
                                                        "group flex cursor-pointer items-start gap-3 rounded-xl p-2 transition-colors duration-200 hover:bg-[#F0F7FF]";
                                                    const tid = `industry-item-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                                                    return (
                                                        <li key={ind.name}>
                                                            {ind.link ? (
                                                                <Link to={ind.link} onClick={() => setOpen(null)} className={cls} data-testid={tid}>
                                                                    {inner}
                                                                </Link>
                                                            ) : (
                                                                <span className={cls} data-testid={tid}>
                                                                    {inner}
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            </>
                                        ) : (
                                            <>
                                                <p className="mt-2 border-b border-[#0F172A]/[0.06] px-2 pb-3 font-mono-brand text-[10px] font-medium uppercase tracking-[0.22em] text-[#94A3B8]" data-testid={`menu-title-${item.id}`}>
                                                    {item.label}
                                                </p>
                                            <ul className="mt-2 space-y-1">
                                                {SIMPLE_MENUS[item.id].map((entry) => (
                                                    <li key={entry.name}>
                                                        {entry.link ? (
                                                            <Link
                                                                to={entry.link}
                                                                onClick={() => setOpen(null)}
                                                                className="block rounded-xl px-4 py-2.5 text-sm font-medium text-[#334155] transition-colors duration-150 hover:bg-[#F0F7FF] hover:text-[#0066FF]"
                                                                data-testid={`simple-item-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                            >
                                                                {entry.name}
                                                            </Link>
                                                        ) : (
                                                            <span className="block cursor-pointer rounded-xl px-4 py-2.5 text-sm font-medium text-[#334155] transition-colors duration-150 hover:bg-[#F0F7FF] hover:text-[#0066FF]">
                                                                {entry.name}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                            </>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </nav>

                <div className="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        aria-label="Search"
                        aria-expanded={searchOpen}
                        onClick={() => setSearchOpen((v) => !v)}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 text-[#94A3B8] transition-colors duration-200 hover:border-[#00E5FF]/40 hover:text-white"
                        data-testid="search-button"
                    >
                        <Search className="h-4 w-4" />
                    </button>
                    <a
                        href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                        className="group hidden items-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-5 py-3 text-xs font-bold text-white shadow-[0_10px_25px_-8px_rgba(0,102,255,0.6)] transition-transform duration-300 hover:scale-[1.04] sm:inline-flex sm:text-sm"
                        data-testid="book-demo-button"
                    >
                        Book a demo
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                    <button
                        type="button"
                        aria-label="Menu"
                        aria-expanded={mobileOpen}
                        onClick={() => setMobileOpen((v) => !v)}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 text-white xl:hidden"
                        data-testid="mobile-menu-button"
                    >
                        {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </div>
            </div>
        </motion.header>

            <AnimatePresence>
                {searchOpen && <SiteSearch onClose={() => setSearchOpen(false)} />}
            </AnimatePresence>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="fixed inset-0 z-[100] flex flex-col bg-[#060A12] xl:hidden"
                        aria-label="Mobile"
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between px-4 py-4 sm:px-10">
                            <Logo compact />
                            <button
                                type="button"
                                aria-label="Close menu"
                                onClick={() => setMobileOpen(false)}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white"
                                data-testid="mobile-menu-close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex-1 space-y-1 overflow-y-auto px-5 py-4">
                            {NAV.map((item) => (
                                <div key={item.id} className="border-b border-white/5 last:border-0">
                                    <button
                                        type="button"
                                        aria-expanded={mobileSection === item.id}
                                        onClick={() => setMobileSection(mobileSection === item.id ? null : item.id)}
                                        className="flex w-full items-center justify-between px-2 py-3.5 text-left"
                                        data-testid={`mobile-section-${item.id}`}
                                    >
                                        <span className="text-base font-bold text-white">{item.label}</span>
                                        <ChevronDown
                                            className={`h-4 w-4 text-[#64748B] transition-transform duration-200 ${mobileSection === item.id ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {mobileSection === item.id && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.22, ease: "easeInOut" }}
                                                className="overflow-hidden"
                                            >
                                                <div className="space-y-1 pb-3 pl-2">
                                                    {(item.mega
                                                        ? MEGA_GROUPS
                                                        : item.solutions
                                                          ? SOLUTIONS_GROUPS
                                                          : [{ title: null, items: item.industries ? INDUSTRY_ITEMS : SIMPLE_MENUS[item.id] }]
                                                    ).map((group) => (
                                                        <div key={group.title ?? "plain"}>
                                                            {group.title && (
                                                                <p className="px-3 pt-2 font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                                                                    {group.title}
                                                                </p>
                                                            )}
                                                            {group.items.map((entry) =>
                                                                entry.link ? (
                                                                    <Link
                                                                        key={entry.name}
                                                                        to={entry.link}
                                                                        onClick={() => setMobileOpen(false)}
                                                                        className="block rounded-lg px-3 py-2.5 text-sm font-medium text-[#CBD5E1] transition-colors hover:bg-white/5 hover:text-white"
                                                                        data-testid={`mobile-item-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                                    >
                                                                        {entry.name}
                                                                    </Link>
                                                                ) : (
                                                                    <span
                                                                        key={entry.name}
                                                                        className="block rounded-lg px-3 py-2.5 text-sm font-medium text-[#64748B]"
                                                                        data-testid={`mobile-item-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                                    >
                                                                        {entry.name}
                                                                    </span>
                                                                ),
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                        <div className="shrink-0 border-t border-white/10 bg-[#060A12] px-5 py-4">
                            <a
                                href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-5 py-4 text-sm font-bold text-white"
                                data-testid="mobile-book-demo-button"
                            >
                                Book a demo
                                <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
